import React from 'react';

import PrimaryButton from '../../Buttons/PrimaryButton';
import {
  clampScore,
  CRITERIA,
  MAX_TOTAL,
  totalScore,
  TPresentationScores,
} from './presentationRules';
import {
  Criterion,
  Footer,
  Max,
  Note,
  Panel,
  PanelTitle,
  ScoreBox,
  ScoreControl,
  Total,
} from './styles';

interface IPresentationFormProps {
  teamName: string;
  subtitle: string;
  scores: TPresentationScores;
  alreadyScored: boolean;
  saving: boolean;
  onChange: (scores: TPresentationScores) => void;
  onSave: () => void;
  /** Cronômetro da equipe (PresentationTimer), controlado por quem usa. */
  timer?: React.ReactNode;
  /** Aviso acima dos critérios (ex.: equipe desclassificada, erro ao salvar). */
  notice?: React.ReactNode;
  /** Celular: o botão de salvar fica fixo no rodapé da folha. */
  stickyFooter?: boolean;
  /** Na folha do celular o cabeçalho já mostra nome e horário (FE-73). */
  hideTitle?: boolean;
}

/** Formulário de um time: os quatro critérios do item 4.2.5 e o total ao vivo. */
const PresentationForm: React.FC<IPresentationFormProps> = ({
  teamName,
  subtitle,
  scores,
  alreadyScored,
  saving,
  onChange,
  onSave,
  timer,
  notice,
  stickyFooter = false,
  hideTitle = false,
}) => {
  const total = totalScore(scores);

  return (
    <Panel aria-labelledby="presentation-team">
      <PanelTitle hidden={hideTitle}>
        <h2 id="presentation-team">{teamName}</h2>
        <p>{subtitle}</p>
      </PanelTitle>

      {timer}
      {notice}

      {CRITERIA.map(criterion => {
        const value = scores[criterion.key];
        const set = (raw: number) =>
          onChange({
            ...scores,
            [criterion.key]: clampScore(criterion.key, raw),
          });

        return (
          <Criterion key={criterion.key}>
            <h3 id={`${criterion.key}-label`}>{criterion.label}</h3>
            <Max>até {criterion.max} pts</Max>
            <ul>
              {criterion.questions.map(question => (
                <li key={question}>{question}</li>
              ))}
            </ul>
            <ScoreControl>
              <input
                type="range"
                min={0}
                max={criterion.max}
                step={1}
                value={value}
                style={
                  {
                    '--fill': `${(value / criterion.max) * 100}%`,
                  } as React.CSSProperties
                }
                aria-labelledby={`${criterion.key}-label`}
                aria-valuetext={`${value} de ${criterion.max} pontos`}
                onChange={e => set(Number(e.target.value))}
                disabled={saving}
              />
              <ScoreBox
                type="text"
                inputMode="numeric"
                aria-label={`${criterion.label}: pontos`}
                value={value}
                onChange={e => set(Number(e.target.value.replace(/\D/g, '')))}
                onFocus={e => e.target.select()}
                disabled={saving}
              />
            </ScoreControl>
          </Criterion>
        );
      })}

      <Footer $sticky={stickyFooter}>
        <Total aria-live="polite">
          <strong>{total}</strong>de {MAX_TOTAL} pontos
        </Total>
        <PrimaryButton
          type="button"
          onClick={onSave}
          loading={saving}
          disabled={saving}
        >
          {alreadyScored ? 'Atualizar avaliação' : 'Salvar avaliação'}
        </PrimaryButton>
      </Footer>
      <Note>
        A nota soma-se aos pontos de arena na classificação da etapa nacional.
        Professores tutores não podem estar na sala durante a apresentação.
      </Note>
    </Panel>
  );
};

export default PresentationForm;
